import React, { useMemo, useState } from 'react'
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'react-bootstrap'
import FormText from 'react-bootstrap/FormText'
import Form from 'react-formal'
import { FormattedMessage, injectIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router'
import * as yup from 'yup'

import { selectCompanyBudgetDimensions } from 'containers/Dimensions/selectors'
import { selectCompanyLoading } from 'containers/Dimensions/selectors'
import SubBudgetSchemeImportForm from 'containers/SubBudgetSchemeImportForm'

import { CancelButton, SuccessButton } from 'components/Button'
import FormField from 'components/FormField'
import SelectDimension from 'components/SelectDimension'

import { useIsSystemUser } from 'utils/hooks/useHasClaim'
import { flattenChildren } from 'utils/schemeGroupUtils'

import messages from './messages'
import styles from './styles.module.css'

export const DUPLICATE_SINGLE = 'copySingle'
export const DUPLICATE_BY_VALUES = 'copyValues'
export const DUPLICATE_BY_TARGETS = 'copyMatrixes'

const RECOMMENDED_MAX_ROW_COUNT = 5000

const getAfterDuplicateRowCount = ({
  form,
  currentRowsCount,
  duplicateTargetRowCount,
}) => {
  if (form.duplicationMode === DUPLICATE_SINGLE)
    return currentRowsCount + duplicateTargetRowCount
  return (
    currentRowsCount + form.dimensionValueIds.length * duplicateTargetRowCount
  )
}

const schema = yup.object({
  duplicationMode: yup.string().default(DUPLICATE_BY_VALUES),
  name: yup.string(),
  dimensionValueIds: yup.array().default([]),
  targetDimension: yup.boolean().default(true),
  matrixDimensionNames: yup
    .array()
    .of(yup.string())
    .default([]),
  matrixes: yup
    .array()
    .of(
      yup.object({
        name: yup.string(),
        dimensionValues: yup
          .array()
          .of(yup.string().nullable())
          .nullable(),
      })
    )
    .default([]),
})

const SubBudgetDuplicateRowModal = ({
  intl: { formatMessage },
  onCancel,
  onSubmit,
  row,
  tree,
}) => {
  const { companyCode } = useParams()
  const [form, setForm] = useState(schema.default())
  const isSystemUser = useIsSystemUser()
  const dimensionsLoading = useSelector(selectCompanyLoading({ companyCode }))
  const currentRowsCount = useMemo(() => flattenChildren(tree)?.size, [tree])
  const duplicateTargetRowCount = useMemo(() => flattenChildren(row)?.size, [
    row,
  ])
  const afterDuplicationRowCount = getAfterDuplicateRowCount({
    form,
    currentRowsCount,
    duplicateTargetRowCount,
  })

  const dimensionData = useSelector((state) =>
    selectCompanyBudgetDimensions(state, { companyCode })
  )

  const submitForm = () => {
    const { dimensionValueIds, ...rest } = form
    const dimensionValues = dimensionData
      .flatMap((dimension) =>
        dimensionValueIds
          .map((id) => {
            const dimensionValue = dimension.dimensionValuesById.get(id)

            if (!dimensionValue) return null

            return {
              id: dimensionValue.id,
              name: dimensionValue.name,
              type: dimension.dimensionType,
            }
          })
          .filter(Boolean)
      )
      .toArray()

    onSubmit({
      ...rest,
      dimensionValues,
      copy: row,
    })
  }

  return (
    <Modal show size="lg">
      <ModalHeader>
        <h4>
          <FormattedMessage {...messages.header} />
          {row.name ? ' - ' + row.name : ''}
        </h4>
      </ModalHeader>
      <ModalBody>
        <Form value={form} onChange={(form) => setForm(form)} schema={schema}>
          <FormField
            name="duplicationMode"
            label={formatMessage(messages.duplicationMode)}
            textField={(item) => formatMessage(messages[item])}
            data={
              isSystemUser
                ? [DUPLICATE_BY_VALUES, DUPLICATE_SINGLE, DUPLICATE_BY_TARGETS]
                : [DUPLICATE_BY_VALUES, DUPLICATE_SINGLE]
            }
            type="selectlist"
          />
          {form.duplicationMode === DUPLICATE_BY_VALUES && (
            <>
              <div className={styles.formGroup}>
                <label htmlFor="duplicateForDimensionValues">
                  {formatMessage(messages.duplicateForDimensionValues)}
                </label>
                <SelectDimension
                  companyCode={companyCode}
                  closeMenuOnSelect={false}
                  dimensionData={dimensionData}
                  inputId="duplicateForDimensionValues"
                  isLoading={dimensionsLoading}
                  onChange={(dimensionValueIds) => {
                    setForm({
                      ...form,
                      dimensionValueIds,
                    })
                  }}
                  selectedDimensionValueIds={form.dimensionValueIds}
                  selectionType="multipleValuesPerDimension"
                  includeOperative={true}
                />
              </div>
              <FormField
                name="targetDimension"
                label={formatMessage(messages.targetDimension)}
              />
            </>
          )}
          {form.duplicationMode === DUPLICATE_SINGLE && (
            <FormField
              autoFocus
              label={formatMessage(messages.name)}
              placeholder={formatMessage(messages.typeName)}
              name="name"
            />
          )}
          {form.duplicationMode === DUPLICATE_BY_TARGETS && (
            <SubBudgetSchemeImportForm
              tree={tree}
              form={form}
              setForm={setForm}
            />
          )}
          {afterDuplicationRowCount > RECOMMENDED_MAX_ROW_COUNT && (
            <FormText>
              <FormattedMessage {...messages.overRecommendedSize} />{' '}
            </FormText>
          )}
        </Form>
      </ModalBody>
      <ModalFooter>
        <CancelButton onClick={onCancel} />
        <SuccessButton onClick={submitForm}>
          <FormattedMessage {...messages.header} />
        </SuccessButton>
      </ModalFooter>
    </Modal>
  )
}

export default injectIntl(SubBudgetDuplicateRowModal)
